/* eslint eslint-comments/no-use: off */

import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import {GitBranchIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useRef} from 'react'

type BranchPickerBranchTarget = {
  id: string
  oid: string
}

export type BranchPickerBranch = {
  id: string
  name: string
  associatedPullRequests: {totalCount: number}
  repository: {
    id: string
    nameWithOwner?: string
    defaultBranchRef?: {
      name: string
      id: string
      target: BranchPickerBranchTarget | null
      associatedPullRequests: {totalCount: number}
    }
  }
  target?: BranchPickerBranchTarget | null
}

export type BranchPickerBaseProps = BaseItemPickerProps<BranchPickerBranch> & {
  activeBranch?: BranchPickerBranch | null
  branches?: BranchPickerBranch[]
  defaultBranchId?: string
}

const DEFAULT_BRANCHES = [] as BranchPickerBranch[]
export function BranchPickerBase({
  activeBranch: initialSelectedItem,
  branches = DEFAULT_BRANCHES,
  defaultBranchId,
  filterItems,
  anchorElement,
  onSelectionChange,
  isLoading = false,
  title,
}: BranchPickerBaseProps) {
  const getItemKey = useCallback((branch: BranchPickerBranch) => branch.id, [])

  const convertToItemProps = useCallback(
    (branch: BranchPickerBranch): ExtendedItemProps<BranchPickerBranch> => ({
      // this is a hack to make sure that we are using the prop
      id: `${branch.id}`,
      children: <span>{branch.name}</span>,
      source: branch,
      leadingVisual: () => <GitBranchIcon />,
      trailingVisual: () => (branch.id === defaultBranchId ? <span>Default</span> : null),
    }),
    [defaultBranchId],
  )

  const branchPickerRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(() => branches.length === 0, [branches])

  return (
    <ItemPicker
      items={branches}
      initialSelectedItems={initialSelectedItem ? [initialSelectedItem] : []}
      filterItems={filterItems}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={LABELS.selectBranch}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      loading={isLoading}
      renderAnchor={anchorProps => anchorElement(anchorProps, branchPickerRef)}
      selectPanelRef={branchPickerRef}
      enforceAtleastOneSelected
      resultListAriaLabel={'Branch results'}
      title={title ?? 'Select a branch'}
      width={'large'}
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No branches were found',
              variant: 'empty',
              body: 'Try searching with a different query for results.',
            }
          : undefined
      }
    />
  )
}

try{ BranchPickerBase.displayName ||= 'BranchPickerBase' } catch {}